// CustomDatePicker.tsx — mobile-first, compact date picker
// Uses react-datepicker (inline calendar) inside a Chakra Popover.
import { CalendarIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Tag,
  Text,
  useDisclosure,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Global, css } from '@emotion/react';
import { endOfDay, format, startOfDay } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSharedUiTranslation } from '../../i18n';
import { buildPresets, type PresetRange } from './date-presets';

/* ─── Compact calendar CSS ─── */

const calendarStyles = css`
  .cdp-cal .react-datepicker {
    font-family: inherit;
    font-size: 0.8rem;
    border: none;
    background: transparent;
  }
  .cdp-cal .react-datepicker__header {
    background: transparent;
    border-bottom: 1px solid var(--chakra-colors-gray-200, #e2e8f0);
    padding: 6px 0 4px;
  }
  .cdp-cal .react-datepicker__current-month {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--chakra-colors-gray-700, #2d3748);
  }
  .cdp-cal .react-datepicker__day-name {
    width: 2rem;
    line-height: 2rem;
    margin: 1px;
    font-size: 0.68rem;
    font-weight: 600;
    color: var(--chakra-colors-gray-500, #718096);
  }
  .cdp-cal .react-datepicker__day {
    width: 2rem;
    line-height: 2rem;
    margin: 1px;
    font-size: 0.78rem;
    border-radius: 50%;
    color: var(--chakra-colors-gray-700, #2d3748);
  }
  .cdp-cal .react-datepicker__day:hover {
    border-radius: 50%;
    background: var(--chakra-colors-blue-50, #ebf8ff);
  }
  .cdp-cal .react-datepicker__day--today {
    font-weight: 700;
  }
  .cdp-cal .react-datepicker__day--selected,
  .cdp-cal .react-datepicker__day--range-start,
  .cdp-cal .react-datepicker__day--range-end {
    background: var(--chakra-colors-blue-500, #3182ce) !important;
    color: #fff !important;
    border-radius: 50% !important;
  }
  .cdp-cal .react-datepicker__day--in-selecting-range,
  .cdp-cal .react-datepicker__day--in-range {
    background: var(--chakra-colors-blue-100, #bee3f8);
    color: var(--chakra-colors-blue-800, #2a4365);
    border-radius: 0;
  }
  .cdp-cal .react-datepicker__day--keyboard-selected {
    background: var(--chakra-colors-blue-50, #ebf8ff);
  }
  .cdp-cal .react-datepicker__day--outside-month {
    color: var(--chakra-colors-gray-300, #cbd5e0);
  }
  .cdp-cal .react-datepicker__navigation {
    top: 6px;
  }
  .cdp-cal .react-datepicker__month {
    margin: 0.2rem 0;
  }
  .cdp-cal .react-datepicker__week-number {
    font-size: 0.65rem;
    color: var(--chakra-colors-gray-400, #a0aec0);
    width: 2rem;
    line-height: 2rem;
  }
  /* Time column */
  .cdp-cal .react-datepicker__time-container {
    border-left: 1px solid var(--chakra-colors-gray-200, #e2e8f0);
    width: 90px !important;
    overflow: visible;
  }
  .cdp-cal .react-datepicker__time-container .react-datepicker__time-box,
  .cdp-cal
    .react-datepicker__time-container
    .react-datepicker__time
    .react-datepicker__time-box {
    width: 90px !important;
  }
  .cdp-cal .react-datepicker__time-container .react-datepicker-time__header {
    overflow: visible;
    text-overflow: unset;
    white-space: nowrap;
    font-size: 0.8rem;
  }
  .cdp-cal .react-datepicker__time-list-item {
    font-size: 0.72rem !important;
    padding: 4px 8px !important;
  }
  .cdp-cal .react-datepicker__time-list-item--selected {
    background: var(--chakra-colors-blue-500, #3182ce) !important;
  }
  /* Force time column to always sit beside the calendar */
  .cdp-cal .react-datepicker {
    display: flex !important;
    flex-direction: row !important;
  }
  .cdp-cal .react-datepicker__month-container {
    flex-shrink: 0;
  }
  .cdp-cal .react-datepicker__time-container {
    flex-shrink: 0;
  }
`;

/* ─── Props (backward-compatible) ─── */

interface CustomDatePickerProps {
  isRange?: boolean;
  onDateChosen?: (startDate: Date, endDate?: Date) => void;
  initialDate?: Date;
  initialEndDate?: Date;
  fontSize?: number;
  onSpecificDateChosen?: (date: Date) => void;
}

/* ─── Component ─── */

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  isRange = false,
  onDateChosen,
  onSpecificDateChosen,
  initialDate,
  initialEndDate,
  fontSize,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useSharedUiTranslation();
  const presets = useMemo(() => buildPresets(), []);
  // Preset labels double as ids (activePreset); translate only for display.
  const presetLabels: Record<string, string> = {
    'Last Hour': t('Last Hour'),
    Today: t('Today'),
    Yesterday: t('Yesterday'),
    'This Week': t('This Week'),
    'Last Week': t('Last Week'),
    'This Month': t('This Month'),
    'Last Month': t('Last Month'),
    'This Year': t('This Year'),
    'Last Year': t('Last Year'),
  };

  const defaultStart =
    initialDate ?? (isRange ? startOfDay(new Date()) : new Date());
  const defaultEnd =
    initialEndDate ?? (isRange ? endOfDay(new Date()) : undefined);

  const [start, setStart] = useState<Date>(defaultStart);
  const [end, setEnd] = useState<Date | undefined>(defaultEnd);
  const [activePreset, setActivePreset] = useState<string | null>(
    isRange ? 'Today' : null,
  );

  // Range mode: two-step picking ("start" then "end")
  const [rangeStep, setRangeStep] = useState<'start' | 'end'>('start');

  /* ── Trigger label ── */
  const triggerLabel = useMemo(() => {
    if (isRange && end) {
      return `${format(start, 'dd.MM.yy HH:mm')} – ${format(end, 'dd.MM.yy HH:mm')}`;
    }
    return format(start, 'dd.MM.yy HH:mm');
  }, [start, end, isRange]);

  /* ── Preset handler ── */
  const selectPreset = useCallback(
    (p: PresetRange) => {
      if (!p.value) {
        // Clear → reset to today
        const now = new Date();
        const todayStart = startOfDay(now);
        const todayEnd = endOfDay(now);
        setStart(todayStart);
        setEnd(todayEnd);
        setActivePreset(null);
        onDateChosen?.(todayStart, todayEnd);
      } else {
        const [s, e] = p.value;
        setStart(s);
        setEnd(e);
        setActivePreset(p.label);
        onDateChosen?.(s, e);
      }
      setRangeStep('start');
      onClose();
    },
    [onDateChosen, onClose],
  );

  /* ── Calendar handlers ── */
  const handleRangeStepChange = useCallback(
    (date: Date | null) => {
      if (!date) return;
      setActivePreset(null);

      if (rangeStep === 'start') {
        setStart(date);
      } else {
        setEnd(date);
      }
    },
    [rangeStep],
  );

  const handleNextStep = useCallback(() => {
    setRangeStep('end');
    if (!end) {
      // Default end to same as start if not yet set
      setEnd(start);
    }
  }, [start, end]);

  const handleApplyRange = useCallback(() => {
    if (start && end) {
      onDateChosen?.(start, end);
    }
    setRangeStep('start');
    onClose();
  }, [start, end, onDateChosen, onClose]);

  const handleSingleChange = useCallback(
    (date: Date | null) => {
      if (!date) return;
      setStart(date);
      setActivePreset(null);
      onSpecificDateChosen?.(date);
      onClose();
    },
    [onSpecificDateChosen, onClose],
  );

  return (
    <>
      <Global styles={calendarStyles} />

      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom-start"
        isLazy>
        <PopoverTrigger>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<CalendarIcon boxSize={3} />}
            fontWeight={500}
            fontSize={fontSize ? `${fontSize}px` : '0.8rem'}
            px={3}
            borderColor="gray.300"
            _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}>
            {triggerLabel}
          </Button>
        </PopoverTrigger>

        <Portal>
          <PopoverContent
            w="auto"
            minW="260px"
            maxW={{ base: '95vw', sm: '400px' }}
            shadow="lg"
            borderRadius="md"
            zIndex={999999}
            _focus={{ outline: 'none' }}>
            <PopoverBody p={3}>
              {/* ── Preset chips (range mode only) ── */}
              {isRange && (
                <>
                  <Wrap spacing={1} mb={2}>
                    {presets.map((p) => (
                      <WrapItem key={p.label}>
                        <Tag
                          size="sm"
                          variant={
                            activePreset === p.label ? 'solid' : 'subtle'
                          }
                          colorScheme={
                            activePreset === p.label ? 'blue' : 'gray'
                          }
                          cursor="pointer"
                          onClick={() => selectPreset(p)}
                          borderRadius="full"
                          px={2}
                          fontSize="0.7rem"
                          _hover={{ opacity: 0.85 }}>
                          {presetLabels[p.label] ?? p.label}
                        </Tag>
                      </WrapItem>
                    ))}
                    <WrapItem>
                      <Tag
                        size="sm"
                        variant="subtle"
                        colorScheme="red"
                        cursor="pointer"
                        onClick={() =>
                          selectPreset({ label: 'Clear', value: null })
                        }
                        borderRadius="full"
                        px={2}
                        fontSize="0.7rem"
                        _hover={{ opacity: 0.85 }}>
                        {t('Clear')}
                      </Tag>
                    </WrapItem>
                  </Wrap>
                  <Divider mb={2} />
                </>
              )}

              {/* ── Calendar ── */}
              <Box className="cdp-cal">
                {isRange ? (
                  <>
                    {/* Step indicator */}
                    <HStack mb={2} spacing={2}>
                      <Tag
                        size="sm"
                        variant={rangeStep === 'start' ? 'solid' : 'outline'}
                        colorScheme="blue"
                        cursor="pointer"
                        onClick={() => setRangeStep('start')}
                        borderRadius="full"
                        px={2}
                        fontSize="0.7rem">
                        {t('From: {{date}}', {
                          date: format(start, 'dd.MM.yy HH:mm'),
                        })}
                      </Tag>
                      <Text fontSize="0.7rem" color="gray.400">
                        →
                      </Text>
                      <Tag
                        size="sm"
                        variant={rangeStep === 'end' ? 'solid' : 'outline'}
                        colorScheme="blue"
                        cursor="pointer"
                        onClick={() => setRangeStep('end')}
                        borderRadius="full"
                        px={2}
                        fontSize="0.7rem">
                        {t('To: {{date}}', {
                          date: end ? format(end, 'dd.MM.yy HH:mm') : '--',
                        })}
                      </Tag>
                    </HStack>
                    <Divider mb={2} />

                    <ReactDatePicker
                      selected={rangeStep === 'start' ? start : (end ?? start)}
                      onChange={handleRangeStepChange}
                      startDate={start}
                      endDate={end}
                      selectsStart={rangeStep === 'start'}
                      selectsEnd={rangeStep === 'end'}
                      minDate={rangeStep === 'end' ? start : undefined}
                      inline
                      showWeekNumbers
                      showTimeSelect
                      timeIntervals={15}
                      timeFormat="HH:mm"
                      timeCaption={t('Time')}
                    />

                    <Flex mt={2} justify="flex-end" gap={2}>
                      {rangeStep === 'start' ? (
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={handleNextStep}
                          fontSize="0.78rem">
                          {t('Next →')}
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRangeStep('start')}
                            fontSize="0.78rem">
                            {t('← Back')}
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            isDisabled={!end}
                            onClick={handleApplyRange}
                            fontSize="0.78rem">
                            {t('Apply')}
                          </Button>
                        </>
                      )}
                    </Flex>
                  </>
                ) : (
                  <ReactDatePicker
                    selected={start}
                    onChange={handleSingleChange}
                    inline
                    showWeekNumbers
                    showTimeSelect
                    timeIntervals={15}
                    timeFormat="HH:mm"
                    timeCaption={t('Time')}
                  />
                )}
              </Box>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </>
  );
};
